import { useEffect, useRef, useState } from 'react';
import {
  getEffectiveReachStatus,
  isReachApproximate,
  isReachPending,
  REACH_POLL_INTERVAL_MS,
  REACH_POLL_TIMEOUT_MS,
  type ReachStatus,
} from '../utils/reach-metric.utils';

type UseReachMetricsPollingOptions = {
  reachStatus?: string | null;
  comparisonReachStatus?: string | null;
  resetKey?: string;
  enabled?: boolean;
  refetch: () => void | Promise<unknown>;
};

export function useReachMetricsPolling({
  reachStatus,
  comparisonReachStatus,
  resetKey,
  enabled = true,
  refetch,
}: UseReachMetricsPollingOptions) {
  const [timedOut, setTimedOut] = useState(false);
  const pollStartRef = useRef<number | null>(null);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    setTimedOut(false);
    pollStartRef.current = null;
  }, [resetKey]);

  const currentPending = enabled && isReachPending(reachStatus);
  const shouldPoll = currentPending || (enabled && isReachPending(comparisonReachStatus));

  useEffect(() => {
    if (!shouldPoll || timedOut) {
      return undefined;
    }

    if (pollStartRef.current === null) {
      pollStartRef.current = Date.now();
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof window.setTimeout> | undefined;

    const scheduleNext = () => {
      timeoutId = window.setTimeout(() => {
        void (async () => {
          if (cancelled) return;
          const startedAt = pollStartRef.current ?? Date.now();
          if (Date.now() - startedAt >= REACH_POLL_TIMEOUT_MS) {
            setTimedOut(true);
            return;
          }
          try {
            await refetchRef.current();
          } catch {
            // Keep polling until timeout; a single failed refetch should not stop the job.
          }
          if (!cancelled) {
            scheduleNext();
          }
        })();
      }, REACH_POLL_INTERVAL_MS);
    };

    scheduleNext();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [resetKey, shouldPoll, timedOut]);

  const effectiveReachStatus = getEffectiveReachStatus(reachStatus, timedOut);

  return {
    effectiveReachStatus,
    isReachLoading: currentPending && !timedOut,
    isReachApproximate: isReachApproximate(reachStatus, timedOut),
    timedOut,
  };
}

export type { ReachStatus };
